import { storeToRefs } from 'pinia'
import {
  createRouter,
  createWebHistory,
  type LocationQuery,
  type RouteRecordRaw
} from 'vue-router'
import { z } from 'zod'
import type { RouterTo } from '@/composables/use-router-utils'
import { LayoutName } from '@/enums/layout-name'
import { RouterName } from '@/enums/router-name'
import { useCheckBoxTypeStore } from '@/stores/use-check-box-type-store'
import { useContractStore } from '@/stores/use-contract-store'
import { useGlobalDialogStore } from '@/stores/use-global-dialog-store'
import { useIdentityStore } from '@/stores/use-identity-store'
import { useStatusStore } from '@/stores/use-status-store'
import { useUserStore } from '@/stores/use-user-store'
import { useLocale } from '~library/composables'
import { ErrorCode, Identity } from '~library/enums'
import { isType } from '~library/utils/functions'

type RouteRecordRawData = RouteRecordRaw & {
  children?: RouteRecordRawData[]
  meta?: {
    auth?: Identity[]
    layout?: LayoutName
  }
}

/** 前端設定正在維護中的頁面 */
const maintainRouter: RouterName[] = []

const router = createRouter({
  scrollBehavior: (to, from) => {
    // 相同頁面 query.tab 改變時，頁面不置頂
    if (to.path === from.path && to.query.tab !== from.query.tab) {
      return false
    }

    return { top: 0, behavior: 'instant' }
  },

  history: createWebHistory(import.meta.env.BASE_URL),
  // 保留 query 值的 + 符號
  parseQuery: (query: string) => {
    return query
      .split('&')
      .map((queryString) => {
        const splitIndex = queryString.indexOf('=')
        const key = queryString.slice(0, splitIndex)
        const value = queryString.slice(splitIndex + 1).replace(/\+/, '+')
        return [key, value]
      })
      .reduce((prev, [key, value]) => {
        if (key && value) {
          prev[key] = value
        }

        return prev
      }, {} as LocationQuery)
  },
  routes: [
    // 首頁, FAQ, 方案介紹, MICE, UATP
    {
      path: '/',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      children: [
        // 首頁
        {
          path: '',
          component: () => import('@/views/home/index.vue'),
          name: RouterName.HOME
        },
        // FAQ
        {
          path: 'faq',
          component: () => import('@/views/faq/index.vue'),
          name: RouterName.FAQ,
          meta: {
            preventRefreshWhenQueryChange: true
          }
        },
        // MICE
        {
          path: 'mice',
          component: () => import('@/views/mice/index.vue'),
          name: RouterName.MICE
        },
        // UATP
        {
          path: 'uatp',
          component: () => import('@/views/uatp/index.vue'),
          name: RouterName.UATP,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT,
              Identity.VISITOR
            ]
          }
        },
        // 我的帳號
        {
          path: 'my-account',
          component: () => import('@/views/my-account/index.vue'),
          name: RouterName.MY_ACCOUNT,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT,
              Identity.TRAVELER
            ],
            preventRefreshWhenQueryChange: true
          }
        }
      ]
    },

    // 企業綁定
    {
      path: '/company-employee',
      meta: {
        layout: LayoutName.MAIN_LAYOUT,
        routerParent: RouterName.COMPANY_EMPLOYEE
      },
      children: [
        // 登入
        {
          path: '',
          component: () => import('@/views/company-employee/index.vue'),
          name: RouterName.COMPANY_EMPLOYEE
        },
        // 企業綁定
        {
          path: 'bind',
          component: () => import('@/views/company-employee/bind-view.vue'),
          name: RouterName.COMPANY_EMPLOYEE_BIND
        },
        // 更換企業綁定
        {
          path: 'change',
          component: () => import('@/views/company-employee/bind-change.vue'),
          name: RouterName.COMPANY_EMPLOYEE_BIND_CHANGE
        },
        // 企業綁定成功
        {
          path: 'success',
          component: () => import('@/views/company-employee/bind-success.vue'),
          name: RouterName.COMPANY_EMPLOYEE_BIND_SUCCESS
        }
      ]
    },
    // 會員尊享
    {
      path: '/exclusive-offer',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      children: [
        {
          path: 'exclusive-offer-list',
          component: () =>
            import('@/views/exclusive-offer/exclusive-offer-list.vue'),
          name: RouterName.EXCLUSIVE_OFFER,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT,
              Identity.TRAVELER
            ]
          }
        },
        {
          path: 'exclusive-offer-detail/:id',
          component: () =>
            import('@/views/exclusive-offer/exclusive-offer-detail.vue'),
          name: RouterName.EXCLUSIVE_OFFER_DETAIL,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT,
              Identity.TRAVELER
            ]
          }
        }
      ]
    },

    // 加入我們
    {
      path: '/join-us',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      children: [
        // 加入我們 - 首頁
        {
          path: '',
          component: () => import('@/views/join-us/index.vue'),
          name: RouterName.JOIN_US
        },
        // 加入我們 - 入會規則與條款 (共用)(1.點我加入 加入表格)(2.企業識別碼 有上一步)(terms.vue)(目前的index)
        {
          path: 'terms',
          component: () => import('@/views/join-us/terms.vue'),
          name: RouterName.JOIN_US_TERMS
        },
        // 加入我們 - 申請入會基本資料(application.vue)
        {
          path: 'application',
          component: () => import('@/views/join-us/apply-form.vue'),
          name: RouterName.JOIN_US_APPLICATION
        },
        // 加入我們 - 申請入會成功頁面
        {
          path: 'application-success',
          component: () => import('@/views/join-us/application-success.vue'),
          name: RouterName.JOIN_US_APPLICATION_SUCCESS
        },
        // 加入我們 - 使用企業辨識碼
        {
          path: 'corporate-identifier',
          component: () => import('@/views/join-us/corporate-identifier.vue'),
          name: RouterName.JOIN_US_CORPORATE_IDENTIFIER
        },
        // 加入我們 - 使用企業辨識碼- 寄送邀請信
        {
          path: 'company-id-invitation',
          component: () =>
            import('@/views/join-us/corporate-identifier-invitation.vue'),
          name: RouterName.JOIN_US_CORPORATE_IDENTIFIER_INVITATION
        },
        // 加入我們 - 使用企業辨識碼 - 成功頁面
        {
          path: 'company-id-success',
          component: () =>
            import('@/views/join-us/corporate-identifier-success.vue'),
          name: RouterName.JOIN_US_CORPORATE_IDENTIFIER_SUCCESS
        }
      ]
    },

    // 更多消息
    {
      path: '/news',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      children: [
        // 最新消息
        {
          path: '',
          component: () => import('@/views/news/index.vue'),
          name: RouterName.NEWS
        },
        // 最新消息 - 內頁
        {
          path: ':id',
          component: () => import('@/views/news/news-detail.vue'),
          name: RouterName.NEWS_DETAIL
        }
      ]
    },

    // bizfam 獨享
    {
      path: '/bizfam-offers',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      children: [
        // 列表
        {
          path: '',
          component: () => import('@/views/bizfam-offers/index.vue'),
          name: RouterName.OFFERS,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT,
              Identity.TRAVELER
            ]
          }
        },
        // 詳細頁
        {
          path: ':id',
          component: () => import('@/views/bizfam-offers/offers-detail.vue'),
          name: RouterName.OFFERS_DETAIL,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT,
              Identity.TRAVELER
            ]
          }
        }
      ]
    },

    // 方案介紹
    {
      path: '/bizfam-introduction',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      component: () => import('@/views/bizfam-introduction/index.vue'),
      name: RouterName.BIZFAM_INTRODUCTION
    },

    // 忘記/更改密碼 RESET_LATCH_KEY_SUCCESS
    {
      path: '/reset-latch-key',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      component: () => import('@/views/reset-latch-key/index.vue'),
      name: RouterName.RESET_LATCH_KEY
    },

    // 忘記/更改密碼 成功頁面
    {
      path: '/reset-latch-key/success',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      component: () => import('@/views/reset-latch-key/success.vue'),
      name: RouterName.RESET_LATCH_KEY_SUCCESS
    },

    // 網站導覽
    {
      path: '/sitemap',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      component: () => import('@/views/sitemap/index.vue'),
      name: RouterName.SITEMAP
    },

    // 差旅經理人 - 員工名單管理
    {
      path: '/staff-management',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      children: [
        {
          path: 'list',
          component: () =>
            import('@/views/staff-management/staff-management.vue'),
          name: RouterName.STAFF_MANAGEMENT,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        },
        {
          path: 'staff-invitation',
          component: () =>
            import('@/views/staff-management/staff-invitation.vue'),
          name: RouterName.STAFF_INVITATION,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        },

        {
          path: 'staff-invitation-record',
          component: () =>
            import('@/views/staff-management/staff-invitation-record.vue'),
          name: RouterName.STAFF_INVITATION_RECORD,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        }
      ]
    },

    // 差旅經理人 - 禮遇兌換管理
    {
      path: '/hospitality-management',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      children: [
        {
          path: 'hospitality-exchange-points',
          component: () =>
            import('@/views/hospitality-management/hospitality-exchange-points.vue'),
          name: RouterName.HOSPITALITY_EXCHANGE_POINTS,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        },
        {
          path: 'hospitality-exchange-list',
          component: () =>
            import('@/views/hospitality-management/hospitality-exchange-list.vue'),
          name: RouterName.HOSPITALITY_EXCHANGE_LIST,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        },
        {
          path: 'hospitality-exchange-detail',
          component: () =>
            import('@/views/hospitality-management/hospitality-exchange-detail.vue'),
          name: RouterName.HOSPITALITY_EXCHANGE_DETAIL,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        },
        {
          path: 'hospitality-assignment',
          component: () =>
            import('@/views/hospitality-management/hospitality-assignment.vue'),
          name: RouterName.HOSPITALITY_ASSIGNMENT,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        },
        {
          path: 'hospitality-agent-arrangement-list',
          component: () =>
            import('@/views/hospitality-management/hospitality-agent-arrangement-list.vue'),
          name: RouterName.HOSPITALITY_AGENT_ARRANGEMENT_LIST,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        },
        {
          path: 'hospitality-agent-arrangement',
          component: () =>
            import('@/views/hospitality-management/hospitality-agent-arrangement.vue'),
          name: RouterName.HOSPITALITY_AGENT_ARRANGEMENT,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        },
        {
          path: 'hospitality-agent-arrangement-detail',
          component: () =>
            import('@/views/hospitality-management/hospitality-agent-arrangement-detail.vue'),
          name: RouterName.HOSPITALITY_AGENT_ARRANGEMENT_DETAIL,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        }
      ]
    },

    // 差旅經理人 - 差旅金額統計
    {
      path: '/business-trip',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      children: [
        {
          path: 'contract-amount',
          component: () => import('@/views/business-trip/contract-amount.vue'),
          name: RouterName.BUSINESS_TRIP_CONTRACT_AMOUNT,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        },
        {
          path: 'history-interval',
          component: () => import('@/views/business-trip/history-interval.vue'),
          name: RouterName.BUSINESS_TRIP_HISTORY_INTERVAL,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        },
        {
          path: 'case-introduction',
          component: () =>
            import('@/views/business-trip/case-introduction.vue'),
          name: RouterName.BUSINESS_TRIP_CASE_INTRODUCTION,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        }
      ]
    },

    // 差旅經理人 - 購物車
    {
      path: '/cart',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      children: [
        {
          path: '',
          component: () => import('@/views/cart/index.vue'),
          name: RouterName.CART,
          meta: {
            auth: [
              Identity.MAIN_MANAGER,
              Identity.CORPORATE_ACCOUNT,
              Identity.CORPORATE_SUB_ACCOUNT
            ]
          }
        }
      ]
    },

    // 差旅人 - 我的行程
    {
      path: '/journey-hospitality-arrangement',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      children: [
        {
          path: 'list',
          component: () =>
            import('@/views/journey-hospitality-arrangement/journey-hospitality-arrangement-list.vue'),
          name: RouterName.JOURNEY_HOSPITALITY_ARRANGEMENT_LIST,
          meta: { auth: [Identity.TRAVELER] }
        },
        {
          path: 'detail',
          component: () =>
            import('@/views/journey-hospitality-arrangement/journey-hospitality-arrangement-detail.vue'),
          name: RouterName.JOURNEY_HOSPITALITY_ARRANGEMENT_DETAIL,
          meta: { auth: [Identity.TRAVELER] }
        }
      ]
    },

    // 差旅人 - 禮遇及兌換券管理
    {
      path: '/treatment',
      meta: {
        layout: LayoutName.MAIN_LAYOUT
      },
      children: [
        {
          path: 'list',
          component: () => import('@/views/treatment/treatment-list.vue'),
          name: RouterName.TREATMENT_LIST,
          meta: {
            auth: [Identity.TRAVELER],
            preventRefreshWhenQueryChange: true
          }
        },
        {
          path: 'history',
          component: () => import('@/views/treatment/treatment-history.vue'),
          name: RouterName.TREATMENT_HISTORY,
          meta: { auth: [Identity.TRAVELER] }
        }
      ]
    },

    // 邀請信
    {
      path: '/invite-mail',
      name: RouterName.INVITE_MAIL,
      component: () => import('@/views/invite-mail/index.vue')
    },

    // 邀請信 - 錯誤
    {
      path: '/invite-error',
      name: RouterName.INVITE_ERROR,
      component: () => import('@/views/invite-error/index.vue'),
      meta: {
        layout: LayoutName.ERROR_LAYOUT
      }
    },

    // 信件預覽
    {
      path: '/mail-preview/:guid',
      name: RouterName.MAIL_PREVIEW,
      component: () => import('@/views/mail-preview/index.vue'),
      meta: {
        layout: LayoutName.BLANK_LAYOUT
      }
    },

    // 登入跳轉頁
    {
      path: '/login-redirect',
      name: RouterName.LOGIN_REDIRECT,
      component: () => import('@/views/login-redirect/index.vue'),
      meta: {
        layout: LayoutName.BLANK_LAYOUT
      }
    },

    {
      path: '/error/:errorCode',
      name: RouterName.ERROR,
      component: () => import('@/views/error/index.vue'),
      meta: {
        layout: LayoutName.ERROR_LAYOUT
      }
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: {
        name: RouterName.ERROR,
        params: {
          errorCode: ErrorCode.NOT_FOUND
        }
      } satisfies RouterTo<RouterName.ERROR>
    }
  ] satisfies RouteRecordRawData[]
})

router.beforeEach(async (to, from, next) => {
  const { updateCorporateAccountData } = useUserStore()
  const { userProfile } = storeToRefs(useUserStore())
  const { identity } = storeToRefs(useIdentityStore())
  const { clearCheckBoxFormData } = useCheckBoxTypeStore()
  const { checkApiStatus } = useStatusStore()
  const { getContractInformation } = useContractStore()
  const { openGlobalDialog } = useGlobalDialogStore()
  const { i18n } = useLocale()

  if (
    isType(to.name, z.nativeEnum(RouterName)) &&
    maintainRouter.length &&
    maintainRouter.includes(to.name)
  ) {
    const { t } = i18n.global

    // 如果正在維護的頁面為首頁時，轉導至系統維護頁
    if (!from.name && to.name === RouterName.HOME) {
      return next({
        name: RouterName.ERROR,
        params: {
          errorCode: ErrorCode.SYSTEM_MAINTAIN
        }
      })
    }

    // 如果一進網頁就在正在維護的畫面時，轉導至首頁
    if (!from.name) {
      return next({ name: RouterName.HOME })
    }

    // 進入到正在維護的頁面時，跳出提示視窗
    openGlobalDialog({
      title: t('SystemMaintain.Title'),
      content: t('SystemMaintain.Message')
    })

    return false
  }

  const auth = to.meta.auth

  // 檢查 API 狀態
  await checkApiStatus()

  /** 不等於 list */
  if (
    to.name !== RouterName.HOSPITALITY_EXCHANGE_DETAIL &&
    to.name !== RouterName.HOSPITALITY_EXCHANGE_LIST
  ) {
    clearCheckBoxFormData()
  }

  // 進入寄送邀請信頁面 - 取得合約資訊
  if (to.name === RouterName.STAFF_INVITATION) {
    await getContractInformation()
  }

  /** false 代表 Identity 全部身份都涵蓋, true 代表有身份限制 */
  const isAuthPage = isType(auth, z.array(z.nativeEnum(Identity)))

  // 無權限訪問需要身分別的頁面時，導回首頁
  if (isAuthPage && !auth.includes(identity.value)) {
    return next({ name: RouterName.HOME })
  }

  // 企業會員登入時
  if (userProfile.value?.identity === Identity.CORPORATE_SUB_ACCOUNT) {
    await updateCorporateAccountData()
  }

  next()
})

export default router
